import * as ModelEvent from "@node-elion/syncron";
import { defaults } from "lodash";

import SubscriptionPool from "../../redux/services/SubscriptionPool";
import { getPRPC } from "../../hooks/usePRPC";
import createRPCQuery from "../../utils/createRPCQuery.util";
import createLogger from "../../utils/logger.util";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import RequiredProperties from "../../types/RequiredProperties";
import Subscription from "../../types/Subscription";
import { Base, Language, TaxiService } from "..";

import { destroyOne } from "./utils";

const logger = createLogger({ name: "Service" });

class Service extends Base {
	static defaultSharedOptions: Service.SharedOptions = {
		deprecate: true,
	};

	static fromResponse(data: any): Service.Model {
		return {
			...data,
			id: data.id,
			active: data.active,
			availableForCar: data.availableForCar,
			availableForExecutor: data.availableForExecutor,

			taxiServices: (data.serviceToTaxiServices || [])
				.map((toTaxiService) => toTaxiService?.taxiService)
				.filter(Boolean)
				.map((taxiService) => TaxiService.fromResponse(taxiService)),

			name: data.name,

			createdAt: data.createdAt,
			updatedAt: data.updatedAt,
			deletedAt: data.deletedAt,
		};
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	static toRequest(model: Service.Model.New | Service.Model.Modified): any {
		return {};
	}

	static toRequestForUpdateService(
		model: Service.Model.New | Service.Model.Modified,
	): any /* Service.updateFormService  */ {
		return {
			name: model.name,
			active: model.active,
			availableForExecutor: model.availableForExecutor,
			availableForCar: model.availableForCar,
			position: model.position,
			//
			// carIds: model.carIds,
			// executorIds: model.executorIds,
			// crewIds: model.crewIds,
			// rateIds: model.rateIds,
			// taxiServiceIds: model.taxiServiceIds,
		};
	}

	static async store(
		object: Service.Model.New,
		options?: Service.StoreOptions,
	) {
		// options = defaults(options, Service.defaultSharedOptions);

		const prpc = getPRPC();

		if (!prpc) return;

		await createRPCQuery(() =>
			prpc.theirsModel.role.create(Service.toRequest(object)),
		);
	}

	static async update(
		object: Service.Model.Modified,
		options?: Service.UpdateOptions,
	) {
		// options = defaults(options, Service.defaultSharedOptions);

		const prpc = getPRPC();

		if (!prpc) return;

		await createRPCQuery(() =>
			prpc.theirsModel.role.update(object.id, Service.toRequest(object)),
		);
	}

	static async updateService(
		object: Service.Model.Modified,
		options?: Service.UpdateOptions,
	) {
		try {
			// options = defaults(options, Service.defaultSharedOptions);

			const prpc = getPRPC();

			if (!prpc && !object?.id) return;

			const request = Service.toRequestForUpdateService(object);

			logger.info("[Service] send:", { id: object.id, object, request });

			const result = await createRPCQuery(() =>
				prpc?.theirsModel?.service?.update(object.id, request),
			)?.then(async (result) => result);

			logger.info("[Service] send true:", { result });
		} catch (err: any) {
			logger.error("[Service] Error updateService:", err);
		}
	}

	static async destroy(
		id: number[] | number,
		options?: Service.DestroyOptions,
	) {
		options = defaults(options, Service.defaultSharedOptions);

		const prpc = getPRPC();

		if (!prpc) return;

		if (Array.isArray(id))
			await Promise.all(id.map((id) => destroyOne(id)));
		else await destroyOne(id);
	}

	public static async subscribe(
		params: Service.SubscribeOptions,
		onUpdate: Subscription.OnUpdate<Service.Model>,
	): Promise<Subscription<Service.SubscribeOptions> | null> {
		const modelEventConstructor = new ModelEvent.ModelEventConstructor({
			onUpdate: (state) => {
				logger.info("[Service] subscribe", state);
				onUpdate({
					...state,
					models: state.models.map(this.fromResponse),
				});
			},
		});
		const subscription = await SubscriptionPool.add(
			async (prpc) => {
				const data = await prpc.theirsModel.service.subscribe({
					params,
					ping: () => true,
					onEvent: (events) => {
						modelEventConstructor.onEvent(events);
					},
					onError: (error) => {
						logger.error(error);
					},
				});
				return data;
			},
			{ name: "Service", metadata: params },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (options: Service.SubscribeOptions) =>
				subscription.update(options),
		} as Subscription<Service.SubscribeOptions>;
	}

	static Global = {
		async index(options: Service.SubscribeOptions) {
			const prpc = getPRPC();

			if (!prpc) return null;
			if (!prpc.theirsModel?.service?.getAll) return null;

			const result = await createRPCQuery(() =>
				prpc.theirsModel.service.getAll({
					limit: options.limit,
					offset: options.offset,
					query: options.query,
					order: options.order,

					taxiServiceIds: options.taxiServiceIds,
					availableForExecutor: options.availableForExecutor,
					availableForCar: options.availableForCar,
				}),
			);

			return {
				cache: result.items.map(Service.fromResponse),
				offset: result.pagination.offset,
				limit: result.pagination.count,
				total: result.pagination.count,
				deprecated: false,
			};
		},
	};
}

declare namespace Service {
	interface Model {
		id: number;
		active?: boolean;
		availableForCar: boolean;
		availableForExecutor: boolean;
		position: Date | number;

		taxiServices?: TaxiService.Model[];
		serviceToTaxiServices?: any[];

		name: Record<Language, string>;

		createdAt: string;
		updatedAt: string;
		deletedAt: string | null;
	}

	interface SubscribeOptions
		extends ServiceSubscribeOptionsBase<Service.Model> {
		taxiServiceIds?: number[];
		availableForExecutor?: boolean;
		availableForCar?: boolean;
	}

	interface SharedOptions {
		deprecate?: boolean;
	}

	interface StoreOptions extends SharedOptions {}
	interface UpdateOptions extends SharedOptions {}
	interface DestroyOptions extends SharedOptions {}

	namespace Model {
		type New = Omit<
			Model,
			"id" | "default" | "createdAt" | "updatedAt" | "deletedAt"
		>;
		type Modified = RequiredProperties<
			Partial<
				Omit<Model, "default" | "createdAt" | "updatedAt" | "deletedAt">
			>,
			"id"
		>;
	}

	interface updateFormService {
		name: Record<string, string>;
		active: boolean;
		availableForExecutor: boolean;
		availableForCar: boolean;
		position: Date | number;
		//
		carIds?: number[];
		executorIds?: number[];
		crewIds?: number[];
		rateIds?: number[];
		taxiServiceIds?: number[];
	}
}

export default Service;
